/**
 * Turning a drop or folder pick into uploadable files: dropped folders are
 * walked into their files (each keeps its folder-relative path, like a
 * `webkitdirectory` pick), operating-system clutter is ignored, and large
 * selections are split into batches the upload path accepts.
 */

const IGNORED_UPLOAD_NAMES = new Set([
  '.ds_store',
  'thumbs.db',
  'desktop.ini',
  '.localized',
  'icon\r',
]);

/** Finder/Explorer metadata a person never means to upload. */
export function isIgnorableUploadFile(file: {
  name: string;
  webkitRelativePath?: string;
}): boolean {
  const path = file.webkitRelativePath || file.name;
  const segments = path.split('/');
  const base = (segments.at(-1) ?? '').toLowerCase();
  return (
    IGNORED_UPLOAD_NAMES.has(base) ||
    base.startsWith('._') ||
    segments.some((segment) => segment === '__MACOSX')
  );
}

/** True when a drop carries at least one folder. */
export function dataTransferHasDirectory(dataTransfer: DataTransfer): boolean {
  return Array.from(dataTransfer.items ?? []).some(
    (item) =>
      item.kind === 'file' &&
      typeof item.webkitGetAsEntry === 'function' &&
      item.webkitGetAsEntry()?.isDirectory === true
  );
}

function withRelativePath(file: File, relativePath: string): File {
  // `webkitRelativePath` is read-only on File; an own property carries the
  // folder path the same way a `webkitdirectory` pick does.
  Object.defineProperty(file, 'webkitRelativePath', {
    value: relativePath,
    configurable: true,
  });
  return file;
}

function readFile(entry: FileSystemFileEntry): Promise<File | null> {
  return new Promise((resolve) => entry.file(resolve, () => resolve(null)));
}

async function readAllEntries(
  directory: FileSystemDirectoryEntry
): Promise<FileSystemEntry[]> {
  const reader = directory.createReader();
  const entries: FileSystemEntry[] = [];
  // readEntries returns at most ~100 entries per call until it returns none.
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve) =>
      reader.readEntries(resolve, () => resolve([]))
    );
    if (batch.length === 0) return entries;
    entries.push(...batch);
  }
}

function isFileEntry(entry: FileSystemEntry): entry is FileSystemFileEntry {
  return entry.isFile;
}

function isDirectoryEntry(
  entry: FileSystemEntry
): entry is FileSystemDirectoryEntry {
  return entry.isDirectory;
}

/**
 * Adds the files under `entry` and reports each empty folder. Returns whether
 * anything (a real file or a reported empty folder) sits under it, so only
 * the innermost empty folder of a chain is reported.
 */
async function walk(
  entry: FileSystemEntry,
  parentPath: string,
  selection: DroppedUploadSelection
): Promise<boolean> {
  const path = parentPath ? `${parentPath}/${entry.name}` : entry.name;
  if (isFileEntry(entry)) {
    const file = await readFile(entry);
    if (!file) return false;
    selection.files.push(parentPath ? withRelativePath(file, path) : file);
    return !isIgnorableUploadFile({
      name: file.name,
      webkitRelativePath: path,
    });
  }
  if (!isDirectoryEntry(entry)) return false;
  let hasContent = false;
  for (const child of await readAllEntries(entry)) {
    if (await walk(child, path, selection)) hasContent = true;
  }
  if (
    !hasContent &&
    !isIgnorableUploadFile({ name: entry.name, webkitRelativePath: path })
  )
    selection.emptyDirectories.push(path);
  return true;
}

/** A drop's files plus the folders in it that hold no file. */
export type DroppedUploadSelection = {
  files: File[];
  /** Folder-relative paths of empty folders, e.g. `Brand/drafts`. */
  emptyDirectories: string[];
};

/**
 * Every file in a drop, folders expanded, and every empty folder in it.
 * Entries are captured synchronously because the browser empties the
 * DataTransfer once the drop event returns.
 */
export async function uploadSelectionFromDataTransfer(
  dataTransfer: DataTransfer
): Promise<DroppedUploadSelection> {
  const entries = Array.from(dataTransfer.items ?? []).flatMap((item) => {
    if (item.kind !== 'file' || typeof item.webkitGetAsEntry !== 'function')
      return [];
    const entry = item.webkitGetAsEntry();
    return entry ? [entry] : [];
  });
  const plainFiles = Array.from(dataTransfer.files ?? []);
  if (!entries.some((entry) => entry.isDirectory))
    return { files: plainFiles, emptyDirectories: [] };
  const selection: DroppedUploadSelection = { files: [], emptyDirectories: [] };
  for (const entry of entries) await walk(entry, '', selection);
  return selection;
}

/** Every file in a drop, folders expanded. */
export async function filesFromDataTransfer(
  dataTransfer: DataTransfer
): Promise<File[]> {
  return (await uploadSelectionFromDataTransfer(dataTransfer)).files;
}

/**
 * Whether a selection must be split before upload. A single file is never
 * split, whatever its size (it uploads in parts), so every batch the planner
 * emits uploads directly instead of being re-batched forever (#5823).
 */
export function needsUploadBatches(
  files: ReadonlyArray<{ size: number }>,
  maxFiles: number,
  maxBytes: number
): boolean {
  if (files.length > maxFiles) return true;
  if (files.length < 2) return false;
  return files.reduce((sum, file) => sum + file.size, 0) > maxBytes;
}

/** Splits files into upload batches within a file count and byte budget. */
export function planUploadBatches<T extends { size: number }>(
  files: readonly T[],
  maxFiles: number,
  maxBytes: number
): T[][] {
  const batches: T[][] = [];
  let current: T[] = [];
  let bytes = 0;
  for (const file of files) {
    if (
      current.length > 0 &&
      (current.length >= maxFiles || bytes + file.size > maxBytes)
    ) {
      batches.push(current);
      current = [];
      bytes = 0;
    }
    current.push(file);
    bytes += file.size;
  }
  if (current.length > 0) batches.push(current);
  return batches;
}
